// One-shot migration — moves any media still living in IndexedDB (legacy
// uploads from before the Cloudinary/R2 wiring landed) up to the right
// cloud service and rewrites the tree's `idb:` refs to public URLs.
//
// The walk is async-recursive: each idb ref is loaded as a Blob, uploaded
// via the right helper based on where it appears in the tree (photo →
// Cloudinary, audio/video file → R2), and the new URL is written into a
// fresh tree object. On individual failures the original ref is left in
// place so a future run can retry.
//
// Caller wires this to a confirm dialog; the migration only runs after
// explicit user consent.

import { uploadPhoto as cloudUploadPhoto, cloudinaryConfigured } from '../config/cloudinary';
import { uploadAV as cloudUploadAV, r2Configured } from '../config/r2Upload';

const IDB_REF_PREFIX = 'idb:';
const isIdbRef = (s) => typeof s === 'string' && s.startsWith(IDB_REF_PREFIX);
const idFromIdbRef = (ref) => ref.slice(IDB_REF_PREFIX.length);

// We avoid importing the IDB helpers from App.js to keep this module
// standalone; reopen the same DB here.
const IDB_NAME = 'gia-pha-media';
const IDB_STORE = 'media';
let _db = null;

function openDb() {
  if (_db) return _db;
  _db = new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _db;
}

async function idbGet(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Walks a tree counting every idb: ref. Used by the boot scan to decide
// whether to surface the migration dialog and to display the item count.
export function countIdbRefs(tree) {
  let n = 0;
  const seenInMedia = (media) => {
    if (!media) return;
    for (const p of media.photos || []) if (isIdbRef(p.src)) n++;
    for (const a of media.audio || []) if (isIdbRef(a.src)) n++;
    for (const v of media.videos || []) {
      if (isIdbRef(v.src)) n++;
      if (isIdbRef(v.thumbnail)) n++;
    }
  };
  const walkPerson = (p) => {
    if (!p) return;
    if (isIdbRef(p.photo)) n++;
    seenInMedia(p.media);
    for (const m of p.marriages || []) {
      seenInMedia(m.media);
      walkPerson(m.spouse);
      for (const c of m.children || []) walkPerson(c);
    }
  };
  walkPerson(tree);
  return n;
}

// Upload one Blob via the right service for its kind. Returns
// { src, key } — `key` is undefined for photos, set for AV uploads.
async function uploadByKind(blob, kind) {
  if (kind === 'photo') {
    if (!cloudinaryConfigured) throw new Error('Cloudinary not configured');
    const folder =
      kind === 'photo' ? 'gia-pha/migrated-photos' : 'gia-pha/migrated';
    const result = await cloudUploadPhoto(blob, { folder });
    return { src: result.url, key: undefined };
  }
  if (kind === 'audio' || kind === 'video') {
    if (!r2Configured) throw new Error('R2 not configured');
    const result = await cloudUploadAV(blob, kind);
    return { src: result.url, key: result.key };
  }
  throw new Error(`Unknown migration kind: ${kind}`);
}

// Migrate one ref. Returns the replacement value plus optional `key`, or
// null if the upload failed (caller leaves the original ref in place).
async function migrateOne(ref, kind, onItemDone) {
  if (!isIdbRef(ref)) return null;
  const id = idFromIdbRef(ref);
  try {
    const blob = await idbGet(id);
    if (!blob) {
      onItemDone({ ok: false, reason: 'blob_missing' });
      return null;
    }
    const uploaded = await uploadByKind(blob, kind);
    await idbDelete(id).catch(() => {});
    onItemDone({ ok: true });
    return uploaded;
  } catch (e) {
    console.warn(`[gia-pha] migration upload failed (${kind}):`, e);
    onItemDone({ ok: false, reason: 'upload_failed', error: e });
    return null;
  }
}

// Walk the tree, replacing every idb: ref with a cloud URL.
// `onProgress({ done, total })` fires after each item attempt (success
// or failure). Returns { tree: rewrittenTree, succeeded, failed }.
export async function migrateTreeIdbToCloud(tree, { onProgress } = {}) {
  const total = countIdbRefs(tree);
  let done = 0;
  let succeeded = 0;
  let failed = 0;

  const tick = (result) => {
    done++;
    if (result?.ok) succeeded++; else failed++;
    onProgress?.({ done, total });
  };

  const migratePhotoItem = async (item) => {
    if (!isIdbRef(item.src)) return item;
    const r = await migrateOne(item.src, 'photo', tick);
    return r ? { ...item, src: r.src } : item;
  };
  const migrateAudioItem = async (item) => {
    if (!isIdbRef(item.src)) return item;
    const r = await migrateOne(item.src, 'audio', tick);
    return r ? { ...item, src: r.src, key: r.key } : item;
  };
  const migrateVideoItem = async (item) => {
    let next = item;
    if (isIdbRef(item.src)) {
      const r = await migrateOne(item.src, 'video', tick);
      if (r) next = { ...next, src: r.src, key: r.key };
    }
    if (isIdbRef(next.thumbnail)) {
      const r = await migrateOne(next.thumbnail, 'photo', tick);
      if (r) next = { ...next, thumbnail: r.src };
    }
    return next;
  };

  const migrateMedia = async (media) => {
    if (!media) return media;
    const out = { ...media };
    if (Array.isArray(media.photos)) {
      out.photos = [];
      for (const p of media.photos) out.photos.push(await migratePhotoItem(p));
    }
    if (Array.isArray(media.audio)) {
      out.audio = [];
      for (const a of media.audio) out.audio.push(await migrateAudioItem(a));
    }
    if (Array.isArray(media.videos)) {
      out.videos = [];
      for (const v of media.videos) out.videos.push(await migrateVideoItem(v));
    }
    return out;
  };

  const migratePerson = async (p) => {
    if (!p) return p;
    const out = { ...p };
    if (isIdbRef(p.photo)) {
      const r = await migrateOne(p.photo, 'photo', tick);
      if (r) out.photo = r.src;
    }
    if (p.media) out.media = await migrateMedia(p.media);
    out.marriages = [];
    for (const m of p.marriages || []) out.marriages.push(await migrateMarriage(m));
    return out;
  };
  const migrateMarriage = async (m) => {
    const out = { ...m };
    if (m.media) out.media = await migrateMedia(m.media);
    out.spouse = await migratePerson(m.spouse);
    out.children = [];
    for (const c of m.children || []) out.children.push(await migratePerson(c));
    return out;
  };

  const migrated = await migratePerson(tree);
  return { tree: migrated, succeeded, failed };
}
